// LogRocket records request and response bodies by default. Bank account details travel in the
// unconditional offer acceptance, direct debit setup and settings payloads, and come back on the
// current-user response, so the bodies are scrubbed before the recorder ships them. Matching on
// field name rather than URL keeps every path covered, including ones added later.
const REDACTED = '[redacted]';

// Every key the portal or backend has used for these values, in camelCase and snake_case.
// Case-insensitive so `bankBSBNumber` and `bankBsbNumber` both match.
const SENSITIVE_KEY_PATTERN = new RegExp(
  '^(' +
    [
      'bsb',
      'bsbNumber',
      'bsb_number',
      'bankBSBNumber',
      'bank_bsb_number',
      'accountNumber',
      'account_number',
      'bankAccountNumber',
      'bank_account_number',
      'accountName',
      'account_name',
      'bankAccountName',
      'bank_account_name',
      'financialInstitutionName',
      'financial_institution_name',
      'bankAccount',
      'bank_account',
    ].join('|') +
    ')$',
  'i',
);

// Only literal JSON-shaped objects are walked. Blobs, ArrayBuffers, Documents and FormData are all
// `typeof 'object'` but carry no own enumerable keys, so recursing into them would replace the body
// with `{}` rather than redact it.
const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

export const redactBankDetails = (value) => {
  if (Array.isArray(value)) return value.map(redactBankDetails);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactBankDetails(entry),
    ]),
  );
};

// LogRocket usually hands over the body as the raw string it saw on the wire, but not always: an XHR
// with `responseType: 'json'`, and its `shouldParseXHRBlob` option, both deliver an already-parsed
// body. Those are redacted in place and handed back as the same type, because the recorder measures
// and stores object bodies itself. Anything else that is not JSON (file uploads, plain text, an
// empty body) passes through untouched.
const redactJsonBody = (body) => {
  if (Array.isArray(body) || isPlainObject(body)) return redactBankDetails(body);
  if (typeof body !== 'string' || body === '') return body;
  try {
    return JSON.stringify(redactBankDetails(JSON.parse(body)));
  } catch (error) {
    return body;
  }
};

// Request and response objects share the same `body` shape, so one sanitizer serves both hooks.
export const sanitizeNetworkBody = (message) => {
  if (!message) return message;
  return { ...message, body: redactJsonBody(message.body) };
};
